import React, { useCallback, useState } from 'react';
import { Calculator, Save, RefreshCw, Download } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import ModelParametersSection from './ModelParametersSection';
import MaterialParametersSection from './MaterialParametersSection';
import PrinterParametersSection from './PrinterParametersSection';
import LaborCostsSection from './LaborCostsSection';
import OverheadsSection from './OverheadsSection';
import ProfitMarginSection from './ProfitMarginSection';

// Импорт утилитарных функций
import { isPositiveNumber, isNonNegativeNumber } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета стоимости 3D-печати
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  materialBreakdown,
  setMaterialBreakdown,
  materialTypes,
  printerTypes,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalcName, setSaveCalcName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = 0) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем данные материала при изменении типа материала
      if (name === 'materialType') {
        const selectedMaterial = materialTypes.find(material => material.id === fieldValue);
        if (selectedMaterial) {
          newState.materialDensity = selectedMaterial.density.toString();
          newState.materialCostPerKg = selectedMaterial.costPerKg.toString();
          
          // Предлагаем также изменить тип принтера, если материал предпочитает конкретный тип
          const preferredPrinterType = selectedMaterial.preferredPrinterType;
          if (preferredPrinterType && prev.printerType !== preferredPrinterType) {
            // Можно установить всплывающее предупреждение или просто изменить автоматически
            // newState.printerType = preferredPrinterType;
          }
        }
      }

      // Обновляем данные принтера при изменении типа принтера
      if (name === 'printerType') {
        const selectedPrinter = printerTypes.find(printer => printer.id === fieldValue);
        if (selectedPrinter) {
          newState.printerCost = selectedPrinter.avgCost.toString();
          newState.printerConsumption = selectedPrinter.powerConsumption.toString();
        }
      }
      
      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeDepreciation' && !checked) {
        newState.depreciationPeriod = '36';
        newState.printerMonthlyUse = '80';
      }
      
      if (name === 'includeFailureRate' && !checked) {
        newState.failureRate = '5';
      }
      
      if (name === 'includeOverheads' && !checked) {
        newState.overheadPercentage = '15';
      }
      
      if (name === 'includeProfit' && !checked) {
        newState.profitMargin = '30';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [materialTypes, printerTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      modelVolume, modelWeight, modelHeight, printQuantity,
      materialType, materialDensity, materialCostPerKg, supportsRequired, supportDensity,
      printerType, printerCost, electricityCost, printerConsumption, printSpeed, layerHeight, nozzleDiameter, infillDensity,
      laborCostPerHour, preparationTime, postProcessingTime,
      includeDepreciation, depreciationPeriod, printerMonthlyUse,
      includeFailureRate, failureRate,
      includeOverheads, overheadPercentage,
      includeProfit, profitMargin
    } = formData;

    // Базовая валидация параметров модели
    if (!isPositiveNumber(modelVolume)) newErrors.modelVolume = 'Объем должен быть положительным';
    if (!isPositiveNumber(modelWeight)) newErrors.modelWeight = 'Вес должен быть положительным';
    if (!isPositiveNumber(modelHeight)) newErrors.modelHeight = 'Высота должна быть положительной';
    if (!isPositiveNumber(printQuantity)) newErrors.printQuantity = 'Количество должно быть положительным';
    if (parseFloatOrDefault(printQuantity) > 1000) newErrors.printQuantity = 'Максимальное количество - 1000 шт.';
    
    // Валидация параметров материала
    if (!materialType) newErrors.materialType = 'Выберите тип материала';
    if (!isPositiveNumber(materialDensity)) newErrors.materialDensity = 'Плотность должна быть положительной';
    if (!isPositiveNumber(materialCostPerKg)) newErrors.materialCostPerKg = 'Стоимость должна быть положительной';
    if (supportsRequired && !isNonNegativeNumber(supportDensity)) newErrors.supportDensity = 'Плотность должна быть неотрицательной';
    if (parseFloatOrDefault(supportDensity) > 100) newErrors.supportDensity = 'Плотность не может превышать 100%';
    
    // Валидация параметров принтера
    if (!printerType) newErrors.printerType = 'Выберите тип принтера';
    if (!isPositiveNumber(printerCost)) newErrors.printerCost = 'Стоимость должна быть положительной';
    if (!isPositiveNumber(electricityCost)) newErrors.electricityCost = 'Стоимость должна быть положительной';
    if (!isPositiveNumber(printerConsumption)) newErrors.printerConsumption = 'Потребление должно быть положительным';
    if (!isPositiveNumber(printSpeed)) newErrors.printSpeed = 'Скорость должна быть положительной';
    if (!isPositiveNumber(layerHeight)) newErrors.layerHeight = 'Высота слоя должна быть положительной';
    if (printerType === 'fdm' && !isPositiveNumber(nozzleDiameter)) newErrors.nozzleDiameter = 'Диаметр сопла должен быть положительным';
    if (!isNonNegativeNumber(infillDensity)) newErrors.infillDensity = 'Плотность должна быть неотрицательной';
    if (parseFloatOrDefault(infillDensity) > 100) newErrors.infillDensity = 'Плотность не может превышать 100%';
    
    // Валидация трудовых затрат
    if (!isPositiveNumber(laborCostPerHour)) newErrors.laborCostPerHour = 'Стоимость должна быть положительной';
    if (!isNonNegativeNumber(preparationTime)) newErrors.preparationTime = 'Время должно быть неотрицательным';
    if (!isNonNegativeNumber(postProcessingTime)) newErrors.postProcessingTime = 'Время должно быть неотрицательным';
    
    // Валидация амортизации
    if (includeDepreciation) {
      if (!isPositiveNumber(depreciationPeriod)) newErrors.depreciationPeriod = 'Период должен быть положительным';
      if (!isPositiveNumber(printerMonthlyUse)) newErrors.printerMonthlyUse = 'Время использования должно быть положительным';
      if (parseFloatOrDefault(printerMonthlyUse) > 720) newErrors.printerMonthlyUse = 'Максимальное время использования в месяц - 720 часов';
    }
    
    // Валидация процента брака
    if (includeFailureRate) {
      if (!isNonNegativeNumber(failureRate)) newErrors.failureRate = 'Процент должен быть неотрицательным';
      if (parseFloatOrDefault(failureRate) > 50) newErrors.failureRate = 'Процент брака слишком высокий (макс. 50%)';
    }
    
    // Валидация накладных расходов и прибыли
    if (includeOverheads) {
      if (!isNonNegativeNumber(overheadPercentage)) newErrors.overheadPercentage = 'Процент должен быть неотрицательным';
      if (parseFloatOrDefault(overheadPercentage) > 200) newErrors.overheadPercentage = 'Процент накладных расходов слишком высокий';
    }
    
    if (includeProfit) {
      if (!isNonNegativeNumber(profitMargin)) newErrors.profitMargin = 'Процент должен быть неотрицательным';
      if (parseFloatOrDefault(profitMargin) > 500) newErrors.profitMargin = 'Маржа прибыли слишком высокая';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Расчет времени печати
  const calculatePrintTime = useCallback((modelHeight, layerHeight, printSpeed) => {
    const heightMm = parseFloatOrDefault(modelHeight) * 10; // Конвертация из см в мм
    const layerHeightMm = parseFloatOrDefault(layerHeight);
    const printSpeedMmSec = parseFloatOrDefault(printSpeed);
    
    if (heightMm <= 0 || layerHeightMm <= 0 || printSpeedMmSec <= 0) {
      return 0;
    }
    
    // Количество слоев
    const layers = Math.ceil(heightMm / layerHeightMm);
    
    // Примерное время на один слой (в секундах)
    // Это упрощенный расчет, в реальности зависит от многих факторов
    const timePerLayer = 60 + (20 / printSpeedMmSec) * 100; // базовое время + время на периметр
    
    // Общее время в часах
    const totalTimeHours = (layers * timePerLayer) / 3600;
    
    return totalTimeHours;
  }, []);

  // Функция расчета стоимости
  const calculateCost = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setMaterialBreakdown([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const modelVolume = parseFloatOrDefault(formData.modelVolume);
        const modelWeight = parseFloatOrDefault(formData.modelWeight);
        const modelHeight = parseFloatOrDefault(formData.modelHeight);
        const printQuantity = parseFloatOrDefault(formData.printQuantity);
        
        const materialDensity = parseFloatOrDefault(formData.materialDensity);
        const materialCostPerKg = parseFloatOrDefault(formData.materialCostPerKg);
        const supportDensity = parseFloatOrDefault(formData.supportDensity) / 100;
        const infillDensity = parseFloatOrDefault(formData.infillDensity) / 100;
        
        const printerCost = parseFloatOrDefault(formData.printerCost);
        const electricityCost = parseFloatOrDefault(formData.electricityCost);
        const printerConsumption = parseFloatOrDefault(formData.printerConsumption);
        const printSpeed = parseFloatOrDefault(formData.printSpeed);
        const layerHeight = parseFloatOrDefault(formData.layerHeight);
        
        const laborCostPerHour = parseFloatOrDefault(formData.laborCostPerHour);
        const preparationTime = parseFloatOrDefault(formData.preparationTime);
        const postProcessingTime = parseFloatOrDefault(formData.postProcessingTime);
        
        const depreciationPeriod = parseFloatOrDefault(formData.depreciationPeriod);
        const printerMonthlyUse = parseFloatOrDefault(formData.printerMonthlyUse);
        const failureRate = parseFloatOrDefault(formData.failureRate) / 100;
        const overheadPercentage = parseFloatOrDefault(formData.overheadPercentage) / 100;
        const profitMargin = parseFloatOrDefault(formData.profitMargin) / 100;
        
        // --- Расчет объема материала ---
        // Объем модели с учетом заполнения
        const modelVolumeEffective = modelVolume * (0.2 + 0.8 * infillDensity); // 20% на внешние стенки + внутреннее заполнение
        
        // Объем поддержек (если нужны)
        const supportVolume = formData.supportsRequired ? modelVolume * 0.3 * supportDensity : 0;
        
        // Общий объем используемого материала в см³
        const totalMaterialVolume = modelVolumeEffective + supportVolume;
        
        // Вес материала в граммах
        const materialWeight = totalMaterialVolume * materialDensity;
        
        // Стоимость материала
        const materialCost = (materialWeight / 1000) * materialCostPerKg;
        
        // --- Расчет времени печати ---
        // Время одной печати в часах
        const singlePrintTime = calculatePrintTime(modelHeight, layerHeight, printSpeed);
        
        // Общее время печати с учетом количества
        const totalPrintTime = singlePrintTime * printQuantity;
        
        // --- Расчет стоимости электроэнергии ---
        // Потребление электроэнергии в кВт⋅ч
        const electricityConsumption = totalPrintTime * printerConsumption;
        
        // Стоимость электроэнергии
        const electricityCostTotal = electricityConsumption * electricityCost;
        
        // --- Расчет амортизации ---
        let depreciationCost = 0;
        if (formData.includeDepreciation) {
          // Стоимость амортизации за час работы принтера
          const hourlyDepreciation = printerCost / (depreciationPeriod * printerMonthlyUse);
          
          // Стоимость амортизации за время печати
          depreciationCost = hourlyDepreciation * totalPrintTime;
        }
        
        // --- Расчет стоимости труда ---
        // Время на подготовку (один раз на весь тираж)
        const totalPreparationTime = preparationTime;
        
        // Время на постобработку (для каждого изделия)
        const totalPostProcessingTime = postProcessingTime * printQuantity;
        
        // Общее рабочее время
        const totalLaborTime = totalPreparationTime + totalPostProcessingTime;
        
        // Стоимость труда
        const laborCost = totalLaborTime * laborCostPerHour;
        
        // --- Учет брака ---
        let failureCost = 0;
        if (formData.includeFailureRate) {
          // Базовая себестоимость без учета накладных и прибыли
          const baseCostPerUnit = (materialCost + electricityCostTotal + depreciationCost + laborCost) / printQuantity;
          
          // Количество ожидаемых бракованных изделий
          const failedItems = printQuantity * failureRate;
          
          // Дополнительная стоимость из-за брака
          failureCost = baseCostPerUnit * failedItems;
        }
        
        // --- Расчет накладных расходов ---
        let overheadsCost = 0;
        if (formData.includeOverheads) {
          // Базовые затраты
          const baseCosts = materialCost + electricityCostTotal + depreciationCost + laborCost + failureCost;
          
          // Накладные расходы
          overheadsCost = baseCosts * overheadPercentage;
        }
        
        // --- Себестоимость ---
        // Общая себестоимость партии
        const totalCostPrice = materialCost + electricityCostTotal + depreciationCost + laborCost + failureCost + overheadsCost;
        
        // Себестоимость за единицу
        const unitCostPrice = totalCostPrice / printQuantity;
        
        // --- Расчет прибыли ---
        let profitAmount = 0;
        let sellingPriceTotal = totalCostPrice;
        let sellingPriceUnit = unitCostPrice;
        
        if (formData.includeProfit) {
          // Прибыль
          profitAmount = totalCostPrice * profitMargin;
          
          // Итоговая стоимость продажи
          sellingPriceTotal = totalCostPrice + profitAmount;
          sellingPriceUnit = sellingPriceTotal / printQuantity;
        }
        
        // --- Структура затрат для визуализации ---
        const materialBreakdownData = [
          { name: 'Материал', value: materialCost, percent: (materialCost / totalCostPrice) * 100 },
          { name: 'Электроэнергия', value: electricityCostTotal, percent: (electricityCostTotal / totalCostPrice) * 100 },
          { name: 'Амортизация', value: depreciationCost, percent: (depreciationCost / totalCostPrice) * 100 },
          { name: 'Труд', value: laborCost, percent: (laborCost / totalCostPrice) * 100 },
          { name: 'Брак', value: failureCost, percent: (failureCost / totalCostPrice) * 100 },
          { name: 'Накладные', value: overheadsCost, percent: (overheadsCost / totalCostPrice) * 100 }
        ].filter(item => item.value > 0);
        
        // --- Подготовка результатов ---
        const finalResults = {
          // Общие параметры
          printQuantity: printQuantity,
          materialType: formData.materialType,
          printerType: formData.printerType,
          
          // Параметры материала
          materialVolume: modelVolumeEffective,
          supportVolume: supportVolume,
          totalMaterialVolume: totalMaterialVolume,
          materialWeight: materialWeight,
          
          // Время
          singlePrintTime: singlePrintTime,
          totalPrintTime: totalPrintTime,
          totalLaborTime: totalLaborTime,
          
          // Затраты по категориям
          materialCost: materialCost,
          electricityCost: electricityCostTotal,
          depreciationCost: depreciationCost,
          laborCost: laborCost,
          failureCost: failureCost,
          overheadsCost: overheadsCost,
          profitAmount: profitAmount,
          
          // Итоговые суммы
          totalCostPrice: totalCostPrice,
          unitCostPrice: unitCostPrice,
          sellingPriceTotal: sellingPriceTotal,
          sellingPriceUnit: sellingPriceUnit,
          
          // Дополнительно
          electricityConsumption: electricityConsumption,
          currency: formData.currency
        };

        setResults(finalResults);
        setMaterialBreakdown(materialBreakdownData);
        
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setMaterialBreakdown([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setMaterialBreakdown, setIsCalculating, calculatePrintTime]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      modelVolume: '100',
      modelWeight: '250',
      modelHeight: '10',
      printQuantity: '1',
      materialType: 'pla',
      materialDensity: '1.24',
      materialCostPerKg: '1500',
      supportsRequired: true,
      supportDensity: '30',
      printerType: 'fdm',
      printerCost: '25000',
      electricityCost: '5.5',
      printerConsumption: '0.25',
      printSpeed: '50',
      layerHeight: '0.2',
      nozzleDiameter: '0.4',
      infillDensity: '20',
      laborCostPerHour: '300',
      preparationTime: '0.5',
      postProcessingTime: '0.5',
      includeDepreciation: true,
      depreciationPeriod: '36',
      printerMonthlyUse: '80',
      includeFailureRate: true,
      failureRate: '5',
      includeOverheads: true,
      overheadPercentage: '15',
      includeProfit: true,
      profitMargin: '30',
      currency: 'RUB',
    });
    setResults(null);
    setMaterialBreakdown([]);
    setErrors({});
    setSaveCalcName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Заголовки для CSV
    const headers = [
      'Параметр', 'Значение'
    ];

    // Строки данных
    const rows = [
      ['Параметры модели и печати:', ''],
      ['Тип материала', materialTypes.find(m => m.id === formData.materialType)?.name || formData.materialType],
      ['Объем модели, см³', formData.modelVolume],
      ['Высота модели, см', formData.modelHeight],
      ['Количество изделий, шт.', formData.printQuantity],
      ['Тип принтера', printerTypes.find(p => p.id === formData.printerType)?.name || formData.printerType],
      ['Плотность заполнения, %', formData.infillDensity],
      ['', ''],
      ['Результаты расчета:', ''],
      ['Объем материала, см³', results.totalMaterialVolume.toFixed(2)],
      ['Вес материала, г', results.materialWeight.toFixed(2)],
      ['Время печати (1 шт.), ч', results.singlePrintTime.toFixed(2)],
      ['Общее время печати, ч', results.totalPrintTime.toFixed(2)],
      ['Расход электроэнергии, кВт·ч', results.electricityConsumption.toFixed(2)],
      ['', ''],
      ['Структура затрат:', ''],
      ['Затраты на материал', results.materialCost.toFixed(2)],
      ['Затраты на электроэнергию', results.electricityCost.toFixed(2)]
    ];

    // Добавляем остальные статьи затрат, если они включены
    if (results.depreciationCost > 0) {
      rows.push(['Амортизация оборудования', results.depreciationCost.toFixed(2)]);
    }
    if (results.laborCost > 0) {
      rows.push(['Трудовые затраты', results.laborCost.toFixed(2)]);
    }
    if (results.failureCost > 0) {
      rows.push(['Затраты на брак', results.failureCost.toFixed(2)]);
    }
    if (results.overheadsCost > 0) {
      rows.push(['Накладные расходы', results.overheadsCost.toFixed(2)]);
    }

    // Итоговые значения
    rows.push(
      ['', ''],
      ['Итоговые суммы:', ''],
      ['Себестоимость (общая)', results.totalCostPrice.toFixed(2)],
      ['Себестоимость (1 шт.)', results.unitCostPrice.toFixed(2)]
    );

    // Добавляем прибыль и продажную цену, если включены
    if (results.profitAmount > 0) {
      rows.push(
        ['Прибыль', results.profitAmount.toFixed(2)],
        ['Продажная цена (общая)', results.sellingPriceTotal.toFixed(2)],
        ['Продажная цена (1 шт.)', results.sellingPriceUnit.toFixed(2)]
      );
    }

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map(row => row.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_стоимости_3d_печати.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalcName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalcName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateCost();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Calculator className="w-6 h-6 text-teal-500" />
          Параметры расчета
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Параметры модели */}
        <ModelParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Параметры материала */}
        <MaterialParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          materialTypes={materialTypes}
        />

        {/* Параметры принтера */}
        <PrinterParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          printerTypes={printerTypes}
        />

        {/* Трудовые затраты */}
        <LaborCostsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Амортизация и брак */}
        <OverheadsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Прибыль */}
        <ProfitMarginSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalcName}
                onChange={(e) => setSaveCalcName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать стоимость'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать расчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;